import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal/modal-options.class';
import { ToasterService } from 'angular2-toaster';
import swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import * as moment from 'moment';

import { EmployeeEmailConfigService } from '../../../services/index';

@Component({
    selector: 'add-edit-employee-email-window',
    templateUrl: './addEditEmployeeEmail.component.html'
})
export class AddEditEmployeeEmailComponent implements OnInit {
    public title: string;
    public rowdata: any = {};
    isLoading: boolean = false;

    @Output() listLoadEvent = new EventEmitter();

    // Excel upload
    @ViewChild('fileUpload') fileUpload;
    isExcelUpload: boolean = false;
    excelData: any;
    rawFile: File = null;
    rawFileName: string = '';
    uploadList: any[] = [];

    constructor(
        public bsModalRef: BsModalRef,
        private employeeEmailConfigService: EmployeeEmailConfigService,
        public toasterService: ToasterService
    ) { }

    ngOnInit(): void {
        if (!this.rowdata.status) {
            this.rowdata.status = 'Active';
        }
        setTimeout(() => { $('#employee_name').focus(); }, 100);
    }

    onEnterKeyPressed(nextEl: string): void {
        $('#' + nextEl).focus();
    }

    onSave(): void {
        if (this.isExcelUpload) {
            this.saveExcelFile();
        } else {
            this.saveManualEntry();
        }
    }

    saveManualEntry(): void {
        let that = this;
        this.isLoading = true;
        this.employeeEmailConfigService
            .addUpdate(this.rowdata)
            .then(function(res: any) {
                that.isLoading = false;
                if (res.success) {
                    swal('SUCCESS', res.message, 'success');
                    that.bsModalRef.hide();
                    that.listLoadEvent.emit();
                } else {
                    that.toasterService.pop({ type: 'error', title: 'ERROR', body: res.message });
                }
            });
    }

    onUploadModeChange(): void {
        if (this.isExcelUpload) { this.clearExcelData(); }
    }

    downloadSampleFile(): void {
        const ws = XLSX.utils.aoa_to_sheet([
            ['EMPLOYEE NAME', 'EMAIL', 'STATUS'],
            ['John Doe', 'john.doe@example.com', 'Active']
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, 'Employee_Email_Config_Sample_' + moment().format('DD-MM-YYYY_HH-mm') + '.xlsx');
    }

    openFileWindow(): void {
        this.fileUpload.nativeElement.click();
    }

    uploadFile(sheet): void {
        let workBook = null;
        let jsonData = null;
        const reader = new FileReader();

        if (sheet.target.files.length === 0) { return; }

        const file = sheet.target.files[0];
        this.rawFile = file;

        reader.onload = (event) => {
            const data = reader.result;
            workBook = XLSX.read(data, { type: 'binary', cellDates: true });
            jsonData = workBook.SheetNames.reduce((initial, name) => {
                const s = workBook.Sheets[name];
                initial[name] = XLSX.utils.sheet_to_json(s);
                return initial;
            }, {});

            if (Object.keys(jsonData).length === 1) {
                this.excelData = JSON.stringify(jsonData);
                this.uploadList = jsonData.Sheet1;
                this.rawFileName = file.name;
                this.validateExcelData();
            } else {
                this.clearExcelData();
                sheet.srcElement.value = null;
                swal({ title: 'ERROR', text: 'Please upload a file with only one sheet', type: 'error' });
            }
        };

        reader.readAsBinaryString(file);
        this.fileUpload.nativeElement.value = '';
    }

    validateExcelData(): void {
        const requiredColumns = ['EMPLOYEE NAME', 'EMAIL', 'STATUS'];
        const errors = [];

        if (this.uploadList.length === 0) { errors.push('Excel file is empty'); }

        const firstRow = this.uploadList[0] || {};
        for (let col of requiredColumns) {
            if (!(col in firstRow)) { errors.push(`Missing required column: ${col}`); }
        }

        for (let i = 0; i < this.uploadList.length; i++) {
            const row = this.uploadList[i];
            const rowNum = i + 2;
            if (!row['EMPLOYEE NAME'] || row['EMPLOYEE NAME'].toString().trim() === '') {
                errors.push(`Row ${rowNum}: Employee Name is required`);
            }
            if (!row['EMAIL'] || row['EMAIL'].toString().trim() === '') {
                errors.push(`Row ${rowNum}: Email is required`);
            } else if (!/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i.test(row['EMAIL'].toString().trim())) {
                errors.push(`Row ${rowNum}: Invalid email address`);
            }
            if (!row['STATUS'] || !['Active', 'InActive'].includes(row['STATUS'])) {
                errors.push(`Row ${rowNum}: Status must be Active or InActive`);
            }
        }

        if (errors.length > 0) {
            swal({ title: 'Validation Errors', html: this.buildErrorHtml(errors), type: 'error' });
            this.clearExcelData();
        }
    }

    buildErrorHtml(errors: string[]): string {
        let html = `<div style="max-height:300px;overflow-y:auto;text-align:left;border:1px solid #ddd;border-radius:4px;padding:6px 10px;">`;
        errors.forEach((err, i) => {
            html += `<div style="padding:5px 0;border-bottom:1px solid #eee;color:#dc3545;font-size:14px;">${i + 1}. ${err}</div>`;
        });
        html += `</div>`;
        return html;
    }

    clearExcelData(): void {
        this.excelData = '';
        this.rawFile = null;
        this.rawFileName = '';
        this.uploadList = [];
    }

    saveExcelFile(): void {
        if (!this.excelData || this.uploadList.length === 0) {
            swal({ title: 'ERROR', text: 'Please upload an Excel file first', type: 'error' });
            return;
        }
        const self = this;
        swal({
            title: 'Confirm Upload',
            text: `Are you sure you want to upload ${this.uploadList.length} record(s)?`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Upload',
            cancelButtonText: 'Cancel'
        }).then(function() {
            self.doUploadExcel();
        }, function(dismiss) {});
    }

    doUploadExcel(): void {
        this.isLoading = true;
        const that = this;
        this.employeeEmailConfigService
            .uploadExcel({ data: this.excelData })
            .then(function(res: any) {
                that.isLoading = false;
                if (res.success) {
                    that.clearExcelData();
                    swal({ title: 'Success', html: that.buildResultHtml(res.message, res.errors), type: 'success' });
                    that.bsModalRef.hide();
                    that.listLoadEvent.emit();
                } else {
                    swal({ title: 'ERROR', html: that.buildResultHtml(res.message, res.errors), type: 'error' });
                }
            }).catch(() => {
                that.isLoading = false;
                swal({ title: 'ERROR', text: 'An error occurred while uploading', type: 'error' });
            });
    }

    buildResultHtml(message: string, errors: string[]): string {
        let html = `<p style="margin-bottom:8px;">${message}</p>`;
        if (errors && errors.length > 0) {
            html += `<div style="max-height:300px;overflow-y:auto;text-align:left;border:1px solid #ddd;border-radius:4px;padding:6px 10px;">`;
            errors.forEach((err, i) => {
                html += `<div style="padding:5px 0;border-bottom:1px solid #eee;color:#dc3545;font-size:15px;font-weight:bold;">${i + 1}. ${err}</div>`;
            });
            html += `</div>`;
        }
        return html;
    }

    fileDataExists(): boolean {
        return !this.excelData;
    }

    isFormInvalid(): boolean {
        if (this.isExcelUpload) {
            return this.fileDataExists();
        }
        return !this.rowdata.employee_name || !this.rowdata.email || !this.rowdata.status;
    }
}



// WEBPACK FOOTER //
// /Applications/MAMP/htdocs/crm-soundsgood/src/app/components/master/employeeEmailConfig/addEditEmployeeEmail.component.ts